// Create Quotation page (route APP_ROUTES.QUOTATION.CREATION = "/quotation-creation").
// Two-step wizard:
//   Step 1 "Quotation": customer name/address + up to 30 item rows (item, quantity, price, net price).
//   Step 2 "Preview & Submit": live PDF preview, pick the WhatsApp contact, then Submit.
// Submit flow: render QuotationPDF -> upload the PDF -> save a quotation log -> send it on WhatsApp
// (template 528) -> navigate back to the Quotation List.
import Tabs from "./tabs";
import { useState, useEffect } from "react";
import {
    Box, Drawer, Typography, IconButton,
    Card,
    TextField,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Stack, CircularProgress,
    Button,
    InputAdornment,
    Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import AddIcon from '@mui/icons-material/Add';
import { useSelector, useDispatch } from "react-redux";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import QuotationPDF from "./crmQuotationPDF";
import moment from "moment";
import { uploadFileThunk } from "../../../components/general/generalThunk";
import { addQuotationLogThunk } from "../quotationThunk";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useNavigate } from "react-router-dom";
import { sendWaConvoTemplateThunk } from "../../messages/messagesThunk";
import { getQuotationLogThunk } from "../quotationThunk";
import ViewQuotationModal from "./viewQuotationModal";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import { FieldSelect } from "../../../components/selections/fieldInputs";
import { QR, QrActionButton, QrModalHeader } from "../../qrCode/components/qrUi";
import { stepCardSx, stepActionsSx, backBtnSx, nextBtnSx, StepHeader } from "../../campaign/components/stepUi";

// Label above an input (same look as FieldTextField / FieldSelect)
const labelSx = {
    display: "block",
    mb: 0.9,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#4b5563",
};

// Rounded input look with blue focus, for the TextFields that need extra props
const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 2.5,
        bgcolor: "#fff",
        fontSize: 14,
        "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ef4444" },
    },
};

// Small rounded icon button (row actions)
const iconBtnSx = (c) => ({
    width: 32,
    height: 32,
    borderRadius: 2,
    color: c,
    bgcolor: `${c}10`,
    border: `1px solid ${c}26`,
    "&:hover": { bgcolor: c, color: "#fff" },
    "&.Mui-disabled": { color: "#cbd5e1", bgcolor: "#f8fafc", borderColor: "#e2e8f0" },
});


// Page component. Reads templates/logs/terms from state.crmQuotation, contacts from state.contacts,
// and the upload status/error from state.general (used for the submit spinner and the error snackbar).
const CrmQuotationCretion = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { quotationTemplates = [], quotationLogs = [], tearmAndConditions = {} } = useSelector((state) => state.crmQuotation);
    const { getContacts = [] } = useSelector((state) => state.contacts || {});
    const { status, error } = useSelector((state) => state.general || {});

    // Builds the next quotation number: "SB/Quote/<sent count + 1>/<current month>/25-26".
    // The financial-year suffix "25-26" is hard-coded.
    const generateNextQuotationNo = (list) => {
        const today = new Date();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        let lastNumber = list.length;

        const nextNumber = lastNumber + 1;
        return `SB/Quote/${nextNumber}/${month}/25-26`;
    };

    // Empty item row. `size` (variant id) is added in handleChange when the item has exactly one variant.
    const initialCard = {
        item: "",
        quantity: "",
        itemPrice: "",
        itemNetPrice: "",
    };

    // All page state in one object: step, item rows, validation errors, customer fields and modal flags.
    const [state, setState] = useState({
        activeStep: 0,
        steps: ["Quotation", "Preview & Submit"],
        quotationCards: [initialCard],
        errors: {},
        userName: "",
        address: "",
        selectContactUser: "",
        quotationNo: "",
        loading: false,
        openQuotationModal: false,
        contactCreationDrawer: false,
        viewTermsAndConditionsModal: false,
        viewitemDetailsModal: false,
        selectedRowData: [],
        // New Contact Adding State
        // The contact fields below appear unused: the contact-creation option in step 2 is commented out.
        contactUserName: "",
        mobileNumber: "",
        selectedDepartmentId: "",
        selectedTags: [],
    });

    const { activeStep, quotationCards, steps, errors, userName, address, selectContactUser, quotationNo, loading, contactCreationDrawer, selectedRowData, viewitemDetailsModal, viewTermsAndConditionsModal, contactUserName, mobileNumber, selectedDepartmentId, selectedTags } = state;

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Quantity dropdown options 1-10.
    const quantityOptions = Array.from({ length: 10 }, (_, i) => ({
        value: i + 1,
        label: `${i + 1}`,
    }));


    // Updates one field of an item row and recalculates dependent values:
    // - item: resets qty/prices; auto-selects the size when the item has a single variant.
    // - quantity: itemPrice = variant amount x qty (only when a size is set; otherwise the price must be typed).
    // - itemNetPrice: must not exceed itemPrice, else a snackbar + row error is shown.
    // On a valid change all errors of that row ("<index>-<field>" keys) are cleared.
    // Note: the row object is mutated in place before setState.
    const handleChange = (index, field, value) => {
        const updatedCards = [...quotationCards];
        const card = updatedCards[index];

        card[field] = value;


        if (field === "item") {
            const selectedItem = quotationTemplates.find(t => t.id === Number(value));

            card.quantity = "";
            card.itemPrice = "";
            card.itemNetPrice = "";

            if (selectedItem && selectedItem.variables.length === 1) {
                card.size = selectedItem.variables[0].id;
            }
        }

        if (field === "quantity") {
            const qty = Number(value) || 0;
            const selectedItem = quotationTemplates.find(t => t.id === Number(card.item));
            if (selectedItem) {
                const selectedSize = selectedItem.variables.find(v => v.id === Number(card.size));
                if (selectedSize) {
                    card.itemPrice = selectedSize.amount * qty;
                }
            }
        }


        if (field === "itemNetPrice") {
            const net = Number(value) || 0;
            const total = Number(card.itemPrice) || 0;

            if (net > total) {

                // 1️⃣ Show Snackbar BEFORE returning
                setSnackbar({
                    open: true,
                    message: "Net price cannot be greater than item price!",
                    severity: "error",
                });

                // 2️⃣ Update errors
                return setState(prev => ({
                    ...prev,
                    quotationCards: updatedCards,
                    errors: {
                        ...prev.errors,
                        [`${index}-itemNetPrice`]: "Net price cannot be greater than item price"
                    }
                }));
            }
        }


        setState(prev => {
            const newErrors = { ...prev.errors };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`${index}-`)) {
                    newErrors[key] = "";
                }
            });

            return {
                ...prev,
                quotationCards: updatedCards,
                errors: newErrors
            };
        });
    };


    // Updates a customer field (userName/address); ignores input that starts with a space.
    const handleCommonFieldsChange = (field, value) => {
        if (value.startsWith(" ")) return;
        setState(prev => ({
            ...prev,
            [field]: value,
            errors: { ...prev.errors, [field]: "" }
        }));
    };

    // Adds an empty item row, only if the last row is fully filled (the Add button is disabled at 30 rows).
    const addCard = () => {
        const lastCard = quotationCards[quotationCards.length - 1];

        if (!validateCard(lastCard, quotationCards.length - 1)) {
            setSnackbar({
                open: true,
                message: "Please fill required fields in active card!",
                severity: "error",
            });
            return;
        }

        setState(prev => ({
            ...prev,
            quotationCards: [...prev.quotationCards, { ...initialCard }],
        }));
    };

    // Removes an item row (never the last remaining one) and clears all errors.
    const removeCard = (index) => {

        if (quotationCards.length === 1) return;
        setState(prev => ({
            ...prev,
            quotationCards: prev.quotationCards.filter((_, i) => i !== index),
            errors: {},
        }));

        setSnackbar({
            open: true,
            message: `Item ${index + 1} deleted successfully!`,
            severity: "success",
        });
    };

    // Flags missing required fields of one row as "<index>-<field>" errors. Returns true when the row is complete.
    const validateCard = (card, index) => {
        let isValid = true;
        let newErrors = { ...errors };

        ["item", "quantity", "itemPrice", "itemNetPrice"].forEach(field => {
            if (!card[field]) {
                newErrors[`${index}-${field}`] = `${field} is required!`;
                isValid = false;
            }
        });

        setState(prev => ({ ...prev, errors: newErrors }));
        return isValid;
    };

    // Step 1 -> Step 2: validates customer name, address and every item row,
    // then generates the quotation number from the current log count.
    const handleNext = () => {
        let allValid = true;
        const newErrors = {};

        if (!userName) {
            newErrors.userName = "Name is required";
            allValid = false;
        }
        if (!address) {
            newErrors.address = "Address is required";
            allValid = false;
        }

        quotationCards.forEach((card, i) => {
            if (!validateCard(card, i)) allValid = false;
        });

        if (!allValid) {

            setState(prev => ({
                ...prev,
                errors: { ...prev.errors, ...newErrors }
            }));

            setSnackbar({
                open: true,
                message: "Please fill all required fields before proceeding!",
                severity: "error",
            });
            return;
        }

        setState(prev => ({
            ...prev,
            activeStep: prev.activeStep + 1
        }));


        if (activeStep === 0) {
            const nextQuotationNo = generateNextQuotationNo(quotationLogs);
            setState(prev => ({ ...prev, quotationNo: nextQuotationNo }));
        }
    };

    // Goes back one step and clears validation errors.
    const handleBack = () => {
        setState(prev => ({
            ...prev,
            activeStep: prev.activeStep - 1,
            errors: {},
        }));
    };



    // Customer fields rendered in step 1 (name max 30 chars, address max 300).
    const quotationCommonFields = [
        { name: "userName", label: "Name", type: "text", value: userName },
        { name: "address", label: "Address", type: "text", value: address },
    ];

    // Field config for each item row. "tearmsbutton" renders the eye icon (item description modal),
    // "button" renders delete. Item labels use the first line of item_desc (text before the first "•").
    const quotationFields = [
        { name: "item", label: "Select Item", type: "select", options: quotationTemplates.map((item) => ({ value: item.id, label: item.item_desc.split("•")[0].replace(/[\r\n]/g, "").trim() || "" })) },
        { name: "quantity", label: "Quantity", type: "select", options: quantityOptions },
        { name: "itemPrice", label: "itemPrice", type: "text", disabled: false },
        { name: "itemNetPrice", label: "Net Price", type: "text" },
        { name: "itemDetails", type: "tearmsbutton" },
        { name: "delete", type: "button" },
    ];

    // Item rows mapped to the shape QuotationPDF / ViewQuotationModal expect; rows without an item are skipped.
    const pdfItems = quotationCards.map(card => {
        const item = quotationTemplates.find(t => t.id === Number(card.item));

        if (!item) return null;

        return {
            title: item.item_desc,
            qty: card.quantity || "01 Nos",
            rate: card?.itemPrice || "0",
            ammount: card?.itemNetPrice || "0"
        };
    }).filter(Boolean);

    // Everything printed on the quotation (company, ref no., date, customer, items, terms, product details).
    // Company details, subject and signatory are hard-coded here.
    const invoiceData = {
        companyName: "SIDDHARTH BROS.",
        companyAddress: "#2-290/1, Plot No 13, Near Municipal Office Jalpally, Hyderabad 05.",
        mobile1: "9848085635",
        mobile2: "9296757890",
        website: "www.flagboards.co.in | www.whiteboardsflag.com",
        email: "siddharthbros@gmail.com",

        ref: `${quotationNo}`,
        date: `${moment(new Date()).format("DD/MM/YYYY")}`,

        receiverName: `Mr.${userName}`,
        receiverCity: `${address}`,

        subject: "Quotation for TOUCH LINE Interactive Flat Panel",

        items: pdfItems,

        terms: tearmAndConditions,
        signedBy: "Vikram Kumar Shah",
        productDetails: quotationTemplates,
    };

    // Converts the generated PDF Blob into a base64 data URL for the upload API.
    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };


    // Final submit (step 2). Requires a contact to be selected, then:
    // 1) renders QuotationPDF to a Blob, 2) uploads it via uploadFileThunk (category "userImages"),
    // 3) saves the log via addQuotationLogThunk, 4) sends WhatsApp template 528 with the PDF URL as replaceUrl,
    // 5) navigates to the Quotation List and forces a logs refresh.
    // Note: the success/error messages are written to `state`, not `snackbar`, so they appear not to be displayed.
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [{ valid: !!selectContactUser, message: "User is required", field: "selectContactUser" }];

        for (let rule of validations) {
            if (!rule.valid) {
                setState(prev => ({
                    ...prev,
                    errors: {
                        ...prev.errors,
                        [rule.field]: rule.message
                    }
                }));

                setSnackbar({
                    open: true,
                    message: rule.message,
                    severity: "error",
                });

                return;
            }
        }

        setState(prev => ({ ...prev, loading: true }));

        try {
            const blob = await pdf(<QuotationPDF data={invoiceData} />).toBlob();
            const base64PDF = await blobToBase64(blob);

            const file = [{ filename: quotationNo, img: base64PDF }];

            // -------------------- UPLOAD FILE --------------------
            const uploadResult = await dispatch(
                uploadFileThunk({
                    credentials: { file, category: "userImages" }
                })
            ).unwrap();

            const fileUrl = uploadResult.data[0].file_url;  // ← FIXED

            // -------------------- SAVE QUOTATION LOG --------------------
            const quotationResult = await dispatch(
                addQuotationLogThunk({
                    credentials: {
                        wa_contact_id: selectContactUser,
                        quotation_path: fileUrl,
                        quotation_no: quotationNo
                    }
                })
            ).unwrap();

            // -------------------- SEND WHATSAPP MESSAGE --------------------
            await dispatch(
                sendWaConvoTemplateThunk({
                    credentials: {
                        receiver_contact_id: selectContactUser,
                        template_id: 528,
                        variables: [],
                        replaceUrl: fileUrl  // ← use real link
                    }
                })
            );

            setState(prev => ({
                ...prev,
                open: true,
                message: quotationResult.message || "Quotation created successfully",
                severity: "success",
            }));

            navigate(APP_ROUTES.QUOTATION.LIST);
            dispatch(getQuotationLogThunk({ type: "crmQuotationLog" }))

        } catch (error) {
            setState((prev) => ({
                ...prev,
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            }));
        } finally {
            setState(prev => ({ ...prev, loading: false }))
        }
    };


    // Generic setter for FieldSelect (used by the "Select User" contact dropdown).
    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // True while submitting (local loading flag + upload status from state.general).
    const isSubmitting = loading && status;

    // Shows the upload API error (state.general) in the snackbar.
    useEffect(() => {
        if (status === "failed")
            setSnackbar({ open: true, message: error, severity: "error" });
    }, [status, error]);


    // Friendly labels for the item fields (display only)
    const fieldLabels = { item: "Item", quantity: "Quantity", itemPrice: "Item Price", itemNetPrice: "Net Price" };

    return (
        <div>
            <Tabs>
                <Box sx={{ width: "100%", pt: 2 }}>
                    <Card elevation={0} sx={stepCardSx}>

                        {/* Stepper */}
                        <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1 }}>
                            <Stepper
                                activeStep={activeStep}
                                alternativeLabel
                                sx={{
                                    "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: QR.blue },
                                    "& .MuiStepLabel-label": { fontSize: 13, fontWeight: 600, mt: "6px !important" },
                                    "& .MuiStepLabel-label.Mui-active": { color: "#0f172a", fontWeight: 700 },
                                }}
                            >
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>

                        {/* Step body: step 1 form or step 2 preview */}
                        <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
                            {activeStep === 0 ? (
                                <Box>
                                    {/* ---------- Customer details: one row ---------- */}
                                    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#f8fafc", border: `1px solid ${QR.border}` }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: QR.blue, bgcolor: `${QR.blue}14` }}>
                                                <PersonOutlineIcon sx={{ fontSize: 17 }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>Customer Details</Typography>
                                        </Stack>
                                        <Grid container spacing={2}>
                                            {
                                                quotationCommonFields.map((field) => (
                                                    <Grid item size={{ xs: 12, sm: 6 }} key={field.name} sx={field.type === "button" && { display: "flex", justifyContent: "flex-end", }}>
                                                        <Typography component="label" sx={labelSx}>
                                                            {field.label}
                                                            <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>*</Box>
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            size={"small"}
                                                            placeholder={`Enter ${field.label.toLowerCase()}`}
                                                            value={field.value}
                                                            minRows={field.name === "address" ? 1 : undefined}
                                                            maxRows={field.name === "address" ? 1 : undefined}
                                                            onChange={(e) => handleCommonFieldsChange(field.name, e.target.value)}
                                                            inputProps={{
                                                                ...(field.name === "userName" && { maxLength: 30 }),
                                                                ...(field.name === "address" && { maxLength: 300 }),
                                                            }}
                                                            error={!!errors[field.name]}
                                                            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], bgcolor: "#fff" } }}
                                                        />
                                                    </Grid>
                                                ))
                                            }
                                        </Grid>
                                    </Box>

                                    {/* ---------- Items ---------- */}
                                    <Box sx={{ mt: 3 }}>
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            alignItems={{ xs: "stretch", sm: "center" }}
                                            justifyContent="space-between"
                                            spacing={1.25}
                                            sx={{ mb: 1.5 }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box sx={{ width: 28, height: 28, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: QR.blue, bgcolor: `${QR.blue}14` }}>
                                                    <Inventory2OutlinedIcon sx={{ fontSize: 17 }} />
                                                </Box>
                                                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>Quotation Items</Typography>
                                                <Box component="span" sx={{ px: 0.9, py: 0.1, borderRadius: 5, fontSize: 12, fontWeight: 800, color: QR.blue, bgcolor: `${QR.blue}12` }}>
                                                    {quotationCards.length}
                                                </Box>
                                            </Stack>
                                            {/* "Add Item" is disabled once 30 rows exist. */}
                                            <div style={{ cursor: quotationCards.length >= 30 ? "not-allowed" : "pointer" }}>
                                                <Button
                                                    sx={{
                                                        width: { xs: "100%", sm: "auto" },
                                                        height: 38,
                                                        px: 1.75,
                                                        borderRadius: 2.5,
                                                        textTransform: "none",
                                                        fontSize: 13.5,
                                                        fontWeight: 700,
                                                        color: QR.blue,
                                                        bgcolor: `${QR.blue}0d`,
                                                        border: `1.5px dashed ${QR.blue}88`,
                                                        "&:hover": { bgcolor: `${QR.blue}1a`, borderColor: QR.blue },
                                                        "&.Mui-disabled": { color: "#94a3b8", borderColor: "#e2e8f0", bgcolor: "#f8fafc" },
                                                    }}
                                                    disabled={quotationCards.length >= 30}
                                                    onClick={addCard}
                                                    startIcon={<AddIcon />}
                                                >
                                                    Add Item
                                                </Button>
                                            </div>
                                        </Stack>

                                        <Stack spacing={1}>
                                            {quotationCards.map((card, rowIndex) => (
                                                <Box
                                                    key={rowIndex}
                                                    sx={{
                                                        // one row per item on desktop: # | item | qty | price | net | actions
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "minmax(0, 1fr)",
                                                            sm: "repeat(2, minmax(0, 1fr))",
                                                            md: "30px minmax(0, 2.2fr) repeat(3, minmax(0, 1fr)) auto",
                                                        },
                                                        alignItems: "end",
                                                        gap: { xs: 1.25, md: 1.5 },
                                                        p: { xs: 1.5, md: 1.25 },
                                                        borderRadius: 2.5,
                                                        bgcolor: "#fff",
                                                        border: `1px solid ${QR.border}`,
                                                        boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                                                        "&:hover": { borderColor: "#cbd5e1" },
                                                    }}
                                                >
                                                    {/* number */}
                                                    <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", justifyContent: "center", height: 42 }}>
                                                        <Box component="span" sx={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: QR.main, bgcolor: `${QR.main}14`, border: `1px solid ${QR.main}33` }}>
                                                            {rowIndex + 1}
                                                        </Box>
                                                    </Box>

                                                    {quotationFields.filter(f => f.type === "select" || f.type === "text").map((field) => {
                                                        // Get selected items except current row
                                                        const selectedItems = quotationCards
                                                            .filter((_, i) => i !== rowIndex)
                                                            .map(c => c.item)
                                                            .filter(Boolean);

                                                        // An item already chosen in another row is removed from this row's dropdown, so each product is listed once.
                                                        const filteredOptions =
                                                            field.name === "item"
                                                                ? field.options.filter(opt => !selectedItems.includes(opt.value))
                                                                : field.options;

                                                        return (
                                                            <Box key={field.name} sx={{ minWidth: 0, gridColumn: { sm: field.name === "item" ? "1 / -1" : "auto", md: "auto" } }}>
                                                                {/* Price fields: digits only, max 10 characters, shown with a rupee prefix. */}
                                                                {field.type === "text" ? (
                                                                    <Box>
                                                                        <Typography component="label" sx={labelSx}>{fieldLabels[field.name] || field.label}</Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            name={field.name}
                                                                            type={field.type}
                                                                            disabled={field.disabled}
                                                                            placeholder="0"
                                                                            value={card[field.name] || ""}
                                                                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
                                                                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                                                            onChange={(e) => {
                                                                                const clean = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                                                                                handleChange(rowIndex, field.name, clean);
                                                                            }}
                                                                            error={!!errors[`${rowIndex}-${field.name}`]}
                                                                            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], height: 42 } }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    <FieldSelect
                                                                        name={field.name}
                                                                        label={field.name === "item" ? `${fieldLabels.item} ${rowIndex + 1}` : (fieldLabels[field.name] || field.label)}
                                                                        // options={field.options}
                                                                        options={filteredOptions}
                                                                        value={card[field.name] || ""}
                                                                        onChange={(name, val) => handleChange(rowIndex, field.name, val)}
                                                                        error={!!errors[`${rowIndex}-${field.name}`]}
                                                                        placeholder={field.label}
                                                                    />
                                                                )}
                                                            </Box>
                                                        );
                                                    })}

                                                    {/* actions */}
                                                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={{ xs: "flex-end", md: "flex-start" }} sx={{ height: { md: 42 }, gridColumn: { sm: "1 / -1", md: "auto" } }}>
                                                        {/* Eye icon opens the item description modal; delete is disabled when only one row remains. */}
                                                        {quotationFields.filter(f => f.type === "tearmsbutton" || f.type === "button").map((field) => (
                                                            field.type === "button" ? (
                                                                <Tooltip key={field.name} title={`Delete This Item ${rowIndex + 1}`}>
                                                                    <span style={{ cursor: quotationCards.length === 1 ? "not-allowed" : "pointer" }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            disabled={quotationCards.length === 1}
                                                                            onClick={() => removeCard(rowIndex)}
                                                                            sx={iconBtnSx("#dc2626")}
                                                                        >
                                                                            <DeleteIcon sx={{ fontSize: 17 }} />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip key={field.name} title={`Viw Item ${rowIndex + 1}`}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setState(prev => ({ ...prev, viewTermsAndConditionsModal: true, selectedRowData: card }))}
                                                                        sx={iconBtnSx(QR.blue)}
                                                                    >
                                                                        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>

                                        {/* Opens the editable Terms & Conditions modal (ViewQuotationModal type "itemDetails"). */}
                                        <Box sx={{ mt: 2, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" }, height: 38, fontSize: 13.5 } }}>
                                            <QrActionButton
                                                icon={<GavelIcon sx={{ fontSize: 16 }} />}
                                                label="Terms & Conditions"
                                                color={QR.main}
                                                onClick={() => setState(prev => ({ ...prev, viewitemDetailsModal: true }))}
                                            />
                                        </Box>
                                    </Box>

                                </Box>
                            ) : (
                                <Box>
                                    {/* ---------- Step 2: preview & submit ---------- */}
                                    <StepHeader icon={<PictureAsPdfOutlinedIcon />} title="Preview & Submit" subtitle="Check the PDF, choose the customer and send" />

                                    {/* Live preview of the same PDF that is uploaded and sent on submit. */}
                                    <Box sx={{ width: '100%', height: { xs: '70vh', md: '90vh' }, borderRadius: 3, overflow: "hidden", border: `1px solid ${QR.border}`, bgcolor: "#f8fafc" }}>
                                        <PDFViewer style={{ width: '100%', height: '100%', border: 0 }}>
                                            <QuotationPDF data={invoiceData} />
                                        </PDFViewer>
                                    </Box>

                                    {/* Preview */}
                                    <Box sx={{ mt: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#f8fafc", border: `1px solid ${QR.border}` }}>
                                        {/* Contact picker (required for submit) + "View Quotation" HTML preview. Option id 3 (contact creation) is commented out. */}
                                        <Grid container spacing={2} alignItems="flex-end">
                                            {
                                                [
                                                    {
                                                        id: 1,
                                                        name: "selectContactUser",
                                                        value: selectContactUser,
                                                        type: "selection",
                                                        label: "Select User",
                                                        options: Array.isArray(getContacts) ? getContacts.map((item) => ({ value: item.id, label: `${item.name} (${item.mobile})` })) : [],
                                                    },
                                                    {
                                                        id: 2,
                                                        type: "button"
                                                    },
                                                    // {
                                                    //     id: 3,
                                                    //     type: "contact-creation"
                                                    // }
                                                ].map((field, index) => (
                                                    <Grid item size={{ xs: 12, md: field.id === 1 ? 6 : "auto" }} key={index} sx={{ "& .MuiButton-root": { width: { xs: "100%", md: "auto" } } }}>
                                                        {
                                                            field.id === 2 ? (
                                                                <QrActionButton
                                                                    icon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                                                                    label="View Quotation"
                                                                    onClick={() => setState(prev => ({ ...prev, openQuotationModal: true }))}
                                                                />
                                                            ) : field.id === 3 ? (
                                                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                                    <Button variant="contained" sx={backBtnSx} onClick={() => setState(prev => ({ ...prev, contactCreationDrawer: true }))} >Contact Creation</Button>
                                                                </Box>
                                                            ) : (
                                                                <FieldSelect
                                                                    name={field.name}
                                                                    label={field.label}
                                                                    options={field.options}
                                                                    value={field.value}
                                                                    onChange={handleSingleSelectChange}
                                                                    error={!!state.errors[field.name]}
                                                                    placeholder="Search contact"
                                                                    icon={<PersonOutlineIcon />}
                                                                    required
                                                                />
                                                            )
                                                        }
                                                    </Grid>
                                                ))
                                            }
                                        </Grid>
                                    </Box>
                                </Box>
                            )}

                            {/* Contact-creation drawer: placeholder content only, and currently unreachable (its button option is commented out). */}
                            {
                                contactCreationDrawer && (
                                    <Drawer
                                        anchor="right"
                                        open={contactCreationDrawer}
                                        onClose={() =>
                                            setState(prev => ({ ...prev, contactCreationDrawer: false }))
                                        }
                                        PaperProps={{
                                            sx: { width: { xs: "100%", sm: 380 } }
                                        }}
                                    >
                                        {/* Header */}
                                        <QrModalHeader
                                            icon={<PersonOutlineIcon />}
                                            title="Contact Creation"
                                            onClose={() =>
                                                setState(prev => ({ ...prev, contactCreationDrawer: false }))
                                            }
                                        />

                                        {/* Drawer content */}
                                        <Box sx={{ p: 2 }}>
                                            <h1>Naresh Tanuri</h1>
                                        </Box>
                                    </Drawer>
                                )
                            }


                            <SnackbarMessage
                                open={snackbar.open}
                                message={snackbar.message}
                                severity={snackbar.severity}
                                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                            />
                        </Box>


                        {/* Navigation Buttons */}
                        <Box sx={stepActionsSx}>
                            <Stack direction="row" spacing={1.5}>
                                <div style={{ cursor: activeStep === 0 ? "not-allowed" : "pointer" }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleBack}
                                        disabled={activeStep === 0}
                                        sx={backBtnSx}
                                    >
                                        Back
                                    </Button>
                                </div>

                                {/* Step 1 shows Next (validates); step 2 shows Submit (PDF upload + log + WhatsApp send). */}
                                {
                                    activeStep === 0 ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNext}
                                            disabled={activeStep >= 2}
                                            sx={nextBtnSx}
                                        >
                                            Next
                                        </Button>
                                    ) : (
                                        <div style={{
                                            cursor: isSubmitting && activeStep >= 2 ? "not-allowed" : "pointer"
                                        }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={activeStep === 0 ? handleNext : isSubmitting ? null : handleSubmit}
                                                disabled={activeStep >= 2}
                                                sx={nextBtnSx}
                                            >
                                                {isSubmitting && (
                                                    <CircularProgress
                                                        size={15}
                                                        sx={{ mr: 1, color: "#fff" }}
                                                    />
                                                )}
                                                {isSubmitting ? "Submitting.." : "Submit"}
                                            </Button>
                                        </div>
                                    )
                                }
                            </Stack>
                        </Box>
                    </Card>
                </Box>
            </Tabs>


            {/* Modals - all ViewQuotationModal with a different `type`: */}
            {/* termsAndCondition = item description (eye icon), itemDetails = edit Terms & Conditions, quotation-Creation = HTML preview. */}
            <>
                {viewTermsAndConditionsModal && (
                    <ViewQuotationModal
                        show={viewTermsAndConditionsModal}
                        handleClose={() => setState(prev => ({ ...prev, viewTermsAndConditionsModal: false }))}
                        rowData={invoiceData}
                        stateData={selectedRowData}
                        type="termsAndCondition"
                    />
                )}

                {viewitemDetailsModal && (
                    <ViewQuotationModal
                        show={viewitemDetailsModal}
                        handleClose={() => setState(prev => ({ ...prev, viewitemDetailsModal: false }))}
                        stateData={selectedRowData}
                        rowData={invoiceData}
                        type="itemDetails"
                    />
                )}

                {state.openQuotationModal && (
                    <ViewQuotationModal
                        show={state.openQuotationModal}
                        handleClose={() => setState(prev => ({ ...prev, openQuotationModal: false }))}
                        rowData={invoiceData}
                        stateData={state}
                        type="quotation-Creation"
                    />
                )}
            </>

        </div>
    );
};
export default CrmQuotationCretion;
